import React, { useRef, useState, useEffect } from "react";
import { applyDrag, snapToGrid, clampToBounds, Rect } from "./geometry";

/**
 * ============================================================================
 * ScreenTile Component Props Interface
 * ============================================================================
 */
export interface ScreenTileProps {
  /** The monitor configuration including unique identifiers, size, and positions */
  screen: {
    x: number;
    y: number;
    width: number;
    height: number;
    monitorId: string;
    computerId: string;
  };
  /** The human-readable name of the parent computer (e.g., "Work Laptop") */
  computerName: string;
  /** Snapping increment in pixels (e.g., 10px). Set to 0 to disable snapping */
  gridSize: number;
  /** Bounding box of the configurator canvas to clamp movement within */
  canvasBounds: Rect;
  /** Flags if the tile is actively being dragged (externally controlled, optional) */
  isDragging?: boolean;
  /** Flags if this tile overlaps with another monitor (renders warning outlines) */
  isOverlapping: boolean;
  /** Callback triggered when a drag drop or arrow key nudge commits a final position */
  onPositionChange: (monitorId: string, x: number, y: number) => void;
  /** Callback triggered during active dragging to update parent layout states smoothly */
  onDragMoveLocal: (monitorId: string, x: number, y: number) => void;
}

/**
 * ScreenTile Component
 * 
 * Represents a single virtual monitor tile placed on the 2D configurator canvas.
 * It supports:
 * 1. Drag-and-drop using HTML5 Pointer Events (pointerdown, pointermove, pointerup) 
 *    with pointer capture so dragging does not break if the cursor moves fast.
 * 2. Keyboard accessibility using Arrow keys (ArrowLeft, ArrowRight, ArrowUp, ArrowDown)
 *    to nudge the selected monitor by the grid increment size.
 * 3. Reactive visual cues: turns cyan when focused/hovered/dragged, red/orange
 *    on overlap collisions, and displays the computer label and monitor specs.
 * 
 * HOW TO TWEAK SETTINGS:
 * - Color styles use global CSS custom properties (variables) defined in index.css.
 * - Change the active glow/ring width by editing the box-shadow px values in the inline styles.
 * - Modify transition timings (e.g. "0.15s ease") to control snap or nudge animation snappiness.
 */
export const ScreenTile: React.FC<ScreenTileProps> = ({
  screen,
  computerName,
  gridSize,
  canvasBounds,
  isDragging: isDraggingExternal = false,
  isOverlapping,
  onPositionChange,
  onDragMoveLocal,
}) => {
  // References to track initial drag coordinates (survives renders without resetting)
  const pointerStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const screenStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  
  // Track whether the pointer is currently pressed down and dragging locally
  const [isDraggingLocal, setIsDraggingLocal] = useState(false);
  
  // Stores the temporary x/y coordinate during active dragging to bypass database latency
  const [localPos, setLocalPos] = useState<{ x: number; y: number } | null>(null);

  // Reference to track keyboard nudge debounce timer
  const nudgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup debounce timer on unmount to prevent memory leaks or state updates on unmounted components
  useEffect(() => {
    return () => {
      if (nudgeTimeoutRef.current) {
        clearTimeout(nudgeTimeoutRef.current);
      }
    };
  }, []);

  // Determine dynamic visual styles and interaction state based on local and external dragging
  const isDraggingAny = isDraggingLocal || isDraggingExternal;

  // Active rendering positions: use temporary drag coords if dragging, else synced database coords
  const renderX = localPos !== null ? localPos.x : screen.x;
  const renderY = localPos !== null ? localPos.y : screen.y;

  /**
   * Pointer Down Event Handler
   * Starts the drag operation, records the start position of both pointer and screen,
   * and requests pointer capture so we don't lose the drag focus.
   */
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Avoid handling multi-touch or right-click events
    if (e.button !== undefined && e.button !== 0) return;

    // Check for and clear any pending keyboard nudge debounce timeouts.
    // Non-coder guide: If a user pressed an arrow key to nudge the monitor, a 500ms timer
    // was scheduled to commit that position. If the user immediately clicks and drags with the mouse,
    // we must cancel the keyboard timer so it doesn't overwrite our mouse movements.
    if (nudgeTimeoutRef.current) {
      clearTimeout(nudgeTimeoutRef.current);
      nudgeTimeoutRef.current = null;
    }

    setIsDraggingLocal(true);
    pointerStart.current = { x: e.clientX, y: e.clientY };
    screenStart.current = { x: screen.x, y: screen.y };
    setLocalPos({ x: screen.x, y: screen.y });

    // Lock the pointer to the target element so move events trigger even if cursor drifts
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  /**
   * Pointer Move Event Handler
   * Calculates the current displacement (dx, dy), computes the new screen position,
   * clamps the tile within the canvas bounds, and reports the change to the parent.
   */
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingLocal) return;

    // Calculate how far the mouse has traveled since the pointer-down event
    const dx = e.clientX - pointerStart.current.x;
    const dy = e.clientY - pointerStart.current.y;

    // Construct the moved rectangle
    const movedRect: Rect = applyDrag(
      {
        x: screenStart.current.x,
        y: screenStart.current.y,
        width: screen.width,
        height: screen.height,
      },
      dx,
      dy
    );

    // Keep the moved rectangle within the bounds of the outer configurator canvas
    const clampedRect = clampToBounds(movedRect, canvasBounds);

    // Update the visual placement immediately
    setLocalPos({ x: clampedRect.x, y: clampedRect.y });

    // Notify the parent configurator canvas to run overlap checks or real-time UI updates
    onDragMoveLocal(screen.monitorId, clampedRect.x, clampedRect.y);
  };

  /**
   * Pointer Up Event Handler
   * Terminates the dragging state, snaps the final coordinates to the grid increment,
   * clamps within bounds, releases pointer capture, and saves to the database.
   */
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingLocal) return;

    // Non-coder guide: Cancel any pending keyboard nudge debounce timeouts when the user
    // completes a drag operation. This ensures that any keyboard nudge timers queued up
    // before or during the drag are cleaned up and will not overwrite the final drag position.
    if (nudgeTimeoutRef.current) {
      clearTimeout(nudgeTimeoutRef.current);
      nudgeTimeoutRef.current = null;
    }

    setIsDraggingLocal(false);
    if (e.currentTarget.releasePointerCapture) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    const dx = e.clientX - pointerStart.current.x;
    const dy = e.clientY - pointerStart.current.y;

    // Compute the final raw position
    const movedRect: Rect = applyDrag(
      {
        x: screenStart.current.x,
        y: screenStart.current.y,
        width: screen.width,
        height: screen.height,
      },
      dx,
      dy
    );

    const clampedRect = clampToBounds(movedRect, canvasBounds);

    // Round the position to the nearest grid step (snapping)
    const snappedPoint = snapToGrid({ x: clampedRect.x, y: clampedRect.y }, gridSize);

    // Ensure the snapped tile is still fully inside the canvas boundaries
    const finalClamped = clampToBounds(
      {
        x: snappedPoint.x,
        y: snappedPoint.y,
        width: screen.width,
        height: screen.height,
      },
      canvasBounds
    );

    // Clear temporary local state
    setLocalPos(null);

    // Commit final placement changes to Convex database/parent state
    onPositionChange(screen.monitorId, finalClamped.x, finalClamped.y);
  };

  /**
   * Keyboard KeyDown Event Handler
   * Allows keyboard users to nudge the active monitor tile with the Arrow keys.
   * Pressing an arrow key moves the screen by the current gridSize increment,
   * clamps and snaps the position, and commits it to the database with a 500ms debounce.
   * 
   * Non-coder guide:
   * - Using arrow keys updates the position instantly on screen using localPos state.
   * - It delays committing the change to the database (onPositionChange) until 500ms after the last keypress.
   * - This prevents database write spam during rapid arrow key pressing.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Non-coder guide: If pointer dragging is currently active (locally or via an external state),
    // we must immediately ignore arrow key events. This prevents concurrent input races where
    // a user presses an arrow key while dragging the monitor with their cursor.
    if (isDraggingLocal || isDraggingExternal) {
      return;
    }

    let dx = 0;
    let dy = 0;

    // Nudge step increment defaults to gridSize (or 10px if grid snapping is off)
    const step = gridSize > 0 ? gridSize : 10;

    switch (e.key) {
      case "ArrowLeft":
        dx = -step;
        break;
      case "ArrowRight":
        dx = step;
        break;
      case "ArrowUp":
        dy = -step;
        break;
      case "ArrowDown":
        dy = step;
        break;
      default:
        return; // Ignore any other key presses
    }

    // Nudge is an accessible interactive operation, prevent browser scrolling
    e.preventDefault();

    // Clear any pending database commits
    if (nudgeTimeoutRef.current) {
      clearTimeout(nudgeTimeoutRef.current);
    }

    // Determine the baseline starting position: use localPos if mid-nudge, otherwise fallback to DB screen coords
    const base = localPos !== null ? localPos : { x: screen.x, y: screen.y };

    // Compute shifted coordinates from base placement
    const movedRect: Rect = applyDrag(
      {
        x: base.x,
        y: base.y,
        width: screen.width,
        height: screen.height,
      },
      dx,
      dy
    );

    const clampedRect = clampToBounds(movedRect, canvasBounds);
    const snappedPoint = snapToGrid({ x: clampedRect.x, y: clampedRect.y }, gridSize);
    
    const finalClamped = clampToBounds(
      {
        x: snappedPoint.x,
        y: snappedPoint.y,
        width: screen.width,
        height: screen.height,
      },
      canvasBounds
    );

    // Update local coordinate state immediately to avoid visual lag on screen
    setLocalPos({ x: finalClamped.x, y: finalClamped.y });

    // Notify parent component about the temporary position change to update overlap indicators instantly
    onDragMoveLocal(screen.monitorId, finalClamped.x, finalClamped.y);

    // Set a debounce timeout to commit coordinates to the Convex database 500ms after the last keypress
    nudgeTimeoutRef.current = setTimeout(() => {
      onPositionChange(screen.monitorId, finalClamped.x, finalClamped.y);
      setLocalPos(null);
      nudgeTimeoutRef.current = null;
    }, 500);
  };

  // Determine dynamic visual styles based on active interaction states
  
  // Border colors based on state: error/red for overlap, cyan for dragging/active, slate for inactive
  let borderColor = "var(--color-border)";
  let shadowStyle = "none";
  let backgroundColor = "var(--color-bg-secondary)";

  if (isOverlapping) {
    borderColor = "var(--color-error)";
    shadowStyle = "0 0 12px rgba(239, 68, 68, 0.4)";
    backgroundColor = "rgba(239, 68, 68, 0.05)";
  } else if (isDraggingAny) {
    borderColor = "var(--color-accent)";
    shadowStyle = "0 0 16px rgba(0, 212, 255, 0.4)";
    backgroundColor = "var(--color-bg-tertiary)";
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Screen ${computerName} - ${screen.monitorId} at x: ${screen.x}, y: ${screen.y}`}
      className={`screen-tile${isDraggingAny ? " is-dragging" : ""}${isOverlapping ? " is-overlapping" : ""}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onKeyDown={handleKeyDown}
      style={{
        position: "absolute",
        left: `${renderX}px`,
        top: `${renderY}px`,
        width: `${screen.width}px`,
        height: `${screen.height}px`,
        borderStyle: "solid",
        borderWidth: "2px",
        borderColor: borderColor,
        borderRadius: "var(--border-radius-md)",
        backgroundColor: backgroundColor,
        boxShadow: shadowStyle,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "0.5rem",
        cursor: isDraggingAny ? "grabbing" : "grab",
        userSelect: "none",
        touchAction: "none", // Prevent scroll/zoom touch gestures from conflicting with dragging
        transition: isDraggingAny ? "none" : "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
        color: "var(--color-text-primary)",
      }}
    >
      {/* Computer Host Name */}
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.85rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        {computerName}
      </div>
      
      {/* Monitor Hardware Reference and Size Label */}
      <div
        style={{
          fontSize: "0.7rem",
          color: "var(--color-text-secondary)",
          marginTop: "0.25rem",
          fontWeight: 500,
        }}
      >
        {screen.monitorId} ({screen.width}x{screen.height})
      </div>

      {/* Overlap Error Flag Indicator */}
      {isOverlapping && (
        <div
          data-testid="overlap-warning"
          style={{
            marginTop: "0.35rem",
            fontSize: "0.65rem",
            color: "var(--color-error)",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Overlap Error
        </div>
      )}
    </div>
  );
};
